import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/SossietyDialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/contexts/I18nContext';
import { useToast } from '@/shared/hooks/use-toast';
import { Trash2 } from 'lucide-react';
import { DialogTitle } from '@/components/ui/dialog';

interface AdminDeletePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
}

const AdminDeletePostModal: React.FC<AdminDeletePostModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      toast({
        title: t('notifications.toasts.admin_reason_required'),
        description: t('notifications.toasts.admin_reason_required_description'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(reason.trim());
      setReason('');
      onClose();
    } catch (error) {
      console.error('Error deleting post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReason('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[80vw] max-w-md bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{t('admin.delete_post_title')}</DialogTitle>
        <SossietyDialogHeader 
          title={t('admin.delete_post_title')} 
          titleClassName="text-lg"
        >
          <SossietyDialogClose onClick={handleClose} />
        </SossietyDialogHeader>

        {/* Icon and Description */}
        <div className="text-center space-y-4 mb-6">
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {t('admin.delete_post_description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="reason" className="text-sm font-medium text-gray-700">
              {t('admin.reason_label')} <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin.reason_placeholder')}
              className="mt-1 min-h-[100px] resize-none"
              disabled={isSubmitting || isLoading}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('admin.reason_help')}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting || isLoading}
              className="flex-1 text-black border-black hover:bg-gray-100 rounded-full"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full"
              disabled={!reason.trim() || isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('admin.deleting')}
                </div>
              ) : (
                t('admin.confirm_deletion')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDeletePostModal;
